import type { LngLat } from "./naver-street-view-url.ts";
import type { NaverStreetViewPov } from "./naver-street-view-url.ts";

const EARTH_R_M = 6371000;

export interface NaverPanoLookupResult {
  panoId: string;
  pov: NaverStreetViewPov;
  snapped: LngLat;
}

interface NearbyPanoramaResponse {
  features?: Array<{
    properties?: {
      id?: string;
      heading?: number;
      tilt?: number;
      fov?: number;
    };
    geometry?: {
      coordinates?: [number, number];
    };
  }>;
}

function haversineMeters(a: LngLat, b: LngLat): number {
  const lat1 = (a.lat * Math.PI) / 180;
  const lat2 = (b.lat * Math.PI) / 180;
  const dLat = lat2 - lat1;
  const dLng = ((b.lng - a.lng) * Math.PI) / 180;
  const sinLat = Math.sin(dLat / 2);
  const sinLng = Math.sin(dLng / 2);
  const h = sinLat * sinLat + Math.cos(lat1) * Math.cos(lat2) * sinLng * sinLng;
  return 2 * EARTH_R_M * Math.asin(Math.min(1, Math.sqrt(h)));
}

/** Nearest Naver street-view panorama (public nearby API, no map render). */
export async function lookupNearestNaverPano(
  _ncpKeyId: string,
  center: LngLat,
  maxSnapM = 120,
): Promise<NaverPanoLookupResult | null> {
  const q = new URLSearchParams({ caller: "naver_map_v3" });
  const url = `https://panorama.map.naver.com/ncp/api/v2/nearby/${center.lng}/${center.lat}/3?${q}`;
  try {
    const res = await fetch(url);
    if (!res.ok) return null;
    const data = (await res.json()) as NearbyPanoramaResponse;
    const feature = data.features?.[0];
    const panoId = feature?.properties?.id?.trim();
    if (!panoId) return null;
    const coords = feature?.geometry?.coordinates;
    const snapped =
      coords && coords.length >= 2
        ? { lng: coords[0], lat: coords[1] }
        : { lng: center.lng, lat: center.lat };
    if (haversineMeters(center, snapped) > maxSnapM) return null;
    const props = feature?.properties;
    return {
      panoId,
      pov: {
        pan: props?.heading ?? 0,
        tilt: props?.tilt ?? 0,
        fov: props?.fov ?? 100,
      },
      snapped,
    };
  } catch {
    return null;
  }
}
